export function willNeverHappen(allCasesHandledValue) {
    throw new Error(`This should not have happened. Unhandled case: ${allCasesHandledValue}`);
}
export function groupBy(arr, key) {
    return arr.reduce((groups, item) => {
        const k = key(item);
        if (k !== null) {
            ;
            (groups[k] || (groups[k] = [])).push(item);
        }
        return groups;
    }, {});
}
export function indexBy(arr, key) {
    return Object.fromEntries(arr.map((item) => [key(item), item]));
}
export function mapValues(object, callbackFn) {
    return Object.fromEntries(Object.entries(object).map(([k, v], index) => [k, callbackFn(v, index, k)]));
}
export function inBatches(arr, size) {
    if (size === undefined || size === null)
        return [arr];
    return Array.from({ length: Math.ceil(arr.length / size) }, (_v, i) => arr.slice(i * size, i * size + size));
}
export function sum(values) {
    return values.reduce((a, v) => a + v, 0);
}
export function sumBigInt(values) {
    return values.reduce((a, v) => a + v, BigInt(0));
}
export function uniqueArray(array) {
    return [...new Set(array)];
}
export function range(size) {
    return [...Array(size).keys()];
}
export function filterMap(values, mapper) {
    return values.map(mapper).filter((value) => value !== null);
}
export function interleave(values, separator) {
    return values.flatMap((value, index) => (index === 0 ? [value] : [separator, value]));
}
export function uniques(listOfStrings) {
    return Array.from(new Set(listOfStrings));
}
export function setDiff(fromList, toList, getIdentifier) {
    const toIdSet = new Set(toList.map(getIdentifier));
    const removed = [];
    const common = [];
    fromList.forEach((item) => {
        const id = getIdentifier(item);
        if (toIdSet.has(id)) {
            toIdSet.delete(id);
            common.push(item);
        }
        else {
            removed.push(item);
        }
    });
    const added = toList.filter((item) => toIdSet.has(getIdentifier(item)));
    return { added, removed, common };
}
export function omit(source, ...properties) {
    const copy = { ...source };
    properties.forEach((property) => delete copy[property]);
    return copy;
}
export function pick(source, ...properties) {
    const result = {};
    properties.forEach((property) => (result[property] = source[property]));
    return result;
}
export function renameKey(source, existingProperty, newProperty) {
    return {
        ...omit(source, existingProperty),
        [newProperty]: source[existingProperty],
    };
}
export function reverseMap(map) {
    return Object.fromEntries(Object.entries(map).map(([key, value]) => [value, key]));
}
export function reverseNumericKeysMap(map) {
    return Object.fromEntries(Object.entries(map).map(([key, value]) => [value, Number(key)]));
}
export function filterType(elements, guard) {
    return elements.filter(guard);
}
export function filterNull(elements) {
    return elements.filter((element) => element !== null);
}
export function filterUndefined(elements) {
    return elements.filter((element) => element !== undefined);
}
export function filterFalse(elements) {
    return elements.filter((element) => element !== false);
}
export function updateSetInclusion(set, item, include) {
    if (include) {
        set.add(item);
    }
    else {
        set.delete(item);
    }
}
export function withoutUndefinedProperties(object) {
    return Object.fromEntries(Object.entries(object).filter(([_key, value]) => value !== undefined));
}
export function withoutUndefinedOrNullProperties(object) {
    return Object.fromEntries(Object.entries(object).filter(([_key, value]) => value !== undefined && value !== null));
}
export function withoutUndefinedNullOrEmptyProperties(object) {
    return Object.fromEntries(Object.entries(object).filter(([_key, value]) => value !== undefined && value !== null && value !== ""));
}
export function isFulfilled(result) {
    return result.status === "fulfilled";
}
export function isRejected(result) {
    return result.status === "rejected";
}
export async function allSettled(values) {
    const results = await Promise.allSettled(values);
    const fulfilled = filterType(results, isFulfilled).map((result) => result.value);
    const rejected = filterType(results, isRejected).map((result) => result.reason);
    return { fulfilled, rejected };
}
export function enumValues(enumObject) {
    return Object.values(enumObject);
}
export function isEnum(enumObject) {
    return (candidate) => {
        return Object.values(enumObject).includes(candidate);
    };
}
export function asEnum(enumObject) {
    return (candidate) => {
        return isEnum(enumObject)(candidate) ? candidate : null;
    };
}
export function tuple(a, b) {
    return [a, b];
}
export async function runParallel(array, taskCreator) {
    return await Promise.all(array.map(taskCreator));
}
export async function runSequential(array, taskCreator) {
    const results = [];
    for (const item of array) {
        results.push(await taskCreator(item));
    }
    return results;
}
export function guidFromBytes16(bytes16) {
    return `${bytes16.substring(2, 10)}-${bytes16.substring(10, 14)}-${bytes16.substring(14, 18)}-${bytes16.substring(18, 22)}-${bytes16.substring(22, 34)}`;
}
export function bytes16FromGuid(guid) {
    const pattern = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i;
    if (!pattern.test(guid)) {
        throw new Error(`Invalid UUID: ${guid}`);
    }
    return `0x${guid.replace(/-/g, "")}`;
}
export function pluralize(value, singular, plural) {
    return value === 1 ? singular : plural;
}
export function dateTimeMinutesOnlyString(date) {
    return date.toLocaleString([], { year: "numeric", month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" });
}
export function getAMPM(date) {
    return date.getHours() >= 12 ? "PM" : "AM";
}
export function dateToTimestampWithoutTimezone(date, delimter1 = "-", delimter2 = " ", delimter3 = ":") {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const seconds = date.getSeconds().toString().padStart(2, "0");
    const d1 = delimter1;
    const d2 = delimter2;
    const d3 = delimter3;
    return `${year}${d1}${month}${d1}${day}${d2}${hours}${d3}${minutes}${d3}${seconds}`;
}
export function dateToTimestampBasic(date) {
    return dateToTimestampWithoutTimezone(date, "", "", "");
}
export function toISODateString(date) {
    return date.toISOString().split("T", 1)[0];
}
export function arrayEquals(array1, array2, isEquals) {
    const _isEquals = isEquals !== null && isEquals !== void 0 ? isEquals : ((item1, item2) => item1 === item2);
    if (array1.length !== array2.length)
        return false;
    return array1.every((element, index) => element === array2[index]);
}
export function getAuthToken(authorizationHeader) {
    if (authorizationHeader === null || authorizationHeader === void 0 ? void 0 : authorizationHeader.startsWith("Bearer ")) {
        return authorizationHeader.substring("Bearer ".length);
    }
    return null;
}
export function validateAndReturnDate(date) {
    if (date instanceof Date && !Number.isNaN(date.getTime())) {
        return date;
    }
    else {
        return null;
    }
}
export function dateFromString(stringDate) {
    const date = new Date(stringDate);
    return validateAndReturnDate(date);
}
export function dateFromStringEpoch(str) {
    if (str && typeof str === "string" && str.length === 13 && !Number.isNaN(str)) {
        const date = new Date(Number.parseInt(str));
        return validateAndReturnDate(date);
    }
    else {
        return null;
    }
}
export function dateFromEpochOrString(str) {
    const date = dateFromStringEpoch(str);
    if (date)
        return date;
    return dateFromString(str);
}
export function dateWithinWeeks(withinWeeks) {
    const date = new Date();
    date.setDate(date.getDate() - 7 * parseInt(withinWeeks));
    return validateAndReturnDate(date);
}
export function toTitleCase(value) {
    return value.replace(/\w\S*/g, (innerValue) => {
        return innerValue.charAt(0).toUpperCase() + innerValue.substring(1).toLowerCase();
    });
}
export function toTitleCaseWithSpaces(value) {
    return toTitleCase(value).replace(/_/g, " ");
}
export function slugify(value) {
    return value
        .toString()
        .toLowerCase()
        .replace(/[^a-zA-Z0-9]/g, "-");
}
export function slugifyOptional(value) {
    if (value === undefined) {
        return undefined;
    }
    return slugify(value);
}
export function unslugify(value) {
    return value.replace(/[^a-zA-Z0-9]/g, "_").toUpperCase();
}
export function unslugifyOptional(value) {
    if (!value) {
        return null;
    }
    return unslugify(value);
}
export const delayAndWait = (ms) => new Promise((res) => setTimeout(res, ms));
export const deepCopy = (target) => {
    if (target === null) {
        return target;
    }
    if (target instanceof Date) {
        return new Date(target.getTime());
    }
    if (target instanceof Array) {
        const cp = [];
        target.forEach((v) => {
            cp.push(v);
        });
        return cp.map((n) => deepCopy(n));
    }
    if (typeof target === "object") {
        const cp = { ...target };
        Object.keys(cp).forEach((k) => {
            cp[k] = deepCopy(cp[k]);
        });
        return cp;
    }
    return target;
};
export function getFullName(user) {
    return filterNull([user.firstName, user.lastName]).join(" ");
}
export function addIgnoreFTUToLink(link) {
    return `${link}?ftu=false`;
}
export const orgLinkPrefix = "org-link";
export function addOrgLink(organizationId, link) {
    const url = new URL(link);
    if (organizationId && url.hash) {
        url.hash = `/${orgLinkPrefix}/${organizationId}${url.hash.slice(1)}`;
    }
    return url.toString();
}
export function extractOrgLink(hash) {
    const parts = hash.split("/");
    let organizationId;
    if (parts[0] === orgLinkPrefix && parts.length > 2) {
        organizationId = parts[1];
    }
    else if (parts[1] === orgLinkPrefix && parts.length > 3 && (parts[0] === "#" || parts[0] === "")) {
        organizationId = parts[2];
    }
    if (organizationId) {
        const result = {
            organizationId,
            link: hash.replace(`${orgLinkPrefix}/${organizationId}/`, ""),
        };
        return result;
    }
    return null;
}
export function addOrgLinkAndIgnoreFTU(organizationId, link) {
    return addOrgLink(organizationId, addIgnoreFTUToLink(link));
}
export function hasWhitespace(input) {
    return /\s/.test(input);
}
export function isKeyOfObject(key, obj) {
    return key in obj;
}
export function includes(coll, el) {
    return coll.includes(el);
}
export function genId(length) {
    let result = "";
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
        counter += 1;
    }
    return result;
}
export function basename(url) {
    var _a, _b;
    return (_b = ((_a = url.split("/").pop()) !== null && _a !== void 0 ? _a : "").split("?")[0]) !== null && _b !== void 0 ? _b : "";
}
export function urlWithoutQueryParams(url) {
    var _a;
    if (!url)
        return null;
    return (_a = url.split("?")[0]) !== null && _a !== void 0 ? _a : "";
}
export function isAlphaNumeric(value) {
    return !!value.match(/^[a-z0-9]+$/gi);
}
export function isPromise(obj) {
    return !!obj && (typeof obj === "object" || typeof obj === "function") && "then" in obj && typeof obj.then === "function";
}
export function getJWTPayload(token) {
    return JSON.parse(atob(token.split(".")[1]));
}
export function getOldDate() {
    const date = new Date();
    date.setFullYear(0);
    return date;
}
export function getDuration(date) {
    return Math.abs(new Date().getTime() - date.getTime());
}
const languageFormats = {
    "af-ZA": "yyyy/MM/dd",
    "am-ET": "d/M/yyyy",
    "ar-AE": "dd/MM/yyyy",
    "ar-BH": "dd/MM/yyyy",
    "ar-DZ": "dd-MM-yyyy",
    "ar-EG": "dd/MM/yyyy",
    "ar-IQ": "dd/MM/yyyy",
    "ar-JO": "dd/MM/yyyy",
    "ar-KW": "dd/MM/yyyy",
    "ar-LB": "dd/MM/yyyy",
    "ar-LY": "dd/MM/yyyy",
    "ar-MA": "dd-MM-yyyy",
    "ar-OM": "dd/MM/yyyy",
    "ar-QA": "dd/MM/yyyy",
    "ar-SA": "dd/MM/yy",
    "ar-SY": "dd/MM/yyyy",
    "ar-TN": "dd-MM-yyyy",
    "ar-YE": "dd/MM/yyyy",
    "arn-CL": "dd-MM-yyyy",
    "as-IN": "dd-MM-yyyy",
    "az-Cyrl-AZ": "dd.MM.yyyy",
    "az-Latn-AZ": "dd.MM.yyyy",
    "ba-RU": "dd.MM.yy",
    "be-BY": "dd.MM.yyyy",
    "bg-BG": "dd.M.yyyy",
    "bn-BD": "dd-MM-yy",
    "bn-IN": "dd-MM-yy",
    "bo-CN": "yyyy/M/d",
    "br-FR": "dd/MM/yyyy",
    "bs-Cyrl-BA": "d.M.yyyy",
    "bs-Latn-BA": "d.M.yyyy",
    "ca-ES": "dd/MM/yyyy",
    "co-FR": "dd/MM/yyyy",
    "cs-CZ": "d.M.yyyy",
    "cy-GB": "dd/MM/yyyy",
    "da-DK": "dd-MM-yyyy",
    "de-AT": "dd.MM.yyyy",
    "de-CH": "dd.MM.yyyy",
    "de-DE": "dd.MM.yyyy",
    "de-LI": "dd.MM.yyyy",
    "de-LU": "dd.MM.yyyy",
    "dsb-DE": "d. M. yyyy",
    "dv-MV": "dd/MM/yy",
    "el-GR": "d/M/yyyy",
    "en-029": "MM/dd/yyyy",
    "en-AU": "d/MM/yyyy",
    "en-BZ": "dd/MM/yyyy",
    "en-CA": "dd/MM/yyyy",
    "en-GB": "dd/MM/yyyy",
    "en-IE": "dd/MM/yyyy",
    "en-IN": "dd-MM-yyyy",
    "en-JM": "dd/MM/yyyy",
    "en-MY": "d/M/yyyy",
    "en-NZ": "d/MM/yyyy",
    "en-PH": "M/d/yyyy",
    "en-SG": "d/M/yyyy",
    "en-TT": "dd/MM/yyyy",
    "en-US": "M/d/yyyy",
    "en-ZA": "yyyy/MM/dd",
    "en-ZW": "M/d/yyyy",
    "es-AR": "dd/MM/yyyy",
    "es-BO": "dd/MM/yyyy",
    "es-CL": "dd-MM-yyyy",
    "es-CO": "dd/MM/yyyy",
    "es-CR": "dd/MM/yyyy",
    "es-DO": "dd/MM/yyyy",
    "es-EC": "dd/MM/yyyy",
    "es-ES": "dd/MM/yyyy",
    "es-GT": "dd/MM/yyyy",
    "es-HN": "dd/MM/yyyy",
    "es-MX": "dd/MM/yyyy",
    "es-NI": "dd/MM/yyyy",
    "es-PA": "MM/dd/yyyy",
    "es-PE": "dd/MM/yyyy",
    "es-PR": "dd/MM/yyyy",
    "es-PY": "dd/MM/yyyy",
    "es-SV": "dd/MM/yyyy",
    "es-US": "M/d/yyyy",
    "es-UY": "dd/MM/yyyy",
    "es-VE": "dd/MM/yyyy",
    "et-EE": "d.MM.yyyy",
    "eu-ES": "yyyy/MM/dd",
    "fa-IR": "MM/dd/yyyy",
    "fi-FI": "d.M.yyyy",
    "fil-PH": "M/d/yyyy",
    "fo-FO": "dd-MM-yyyy",
    "fr-BE": "d/MM/yyyy",
    "fr-CA": "yyyy-MM-dd",
    "fr-CH": "dd.MM.yyyy",
    "fr-FR": "dd/MM/yyyy",
    "fr-LU": "dd/MM/yyyy",
    "fr-MC": "dd/MM/yyyy",
    "fy-NL": "d-M-yyyy",
    "ga-IE": "dd/MM/yyyy",
    "gd-GB": "dd/MM/yyyy",
    "gl-ES": "dd/MM/yy",
    "gsw-FR": "dd/MM/yyyy",
    "gu-IN": "dd-MM-yy",
    "ha-Latn-NG": "d/M/yyyy",
    "he-IL": "dd/MM/yyyy",
    "hi-IN": "dd-MM-yyyy",
    "hr-BA": "d.M.yyyy.",
    "hr-HR": "d.M.yyyy",
    "hsb-DE": "d. M. yyyy",
    "hu-HU": "yyyy. MM. dd.",
    "hy-AM": "dd.MM.yyyy",
    "id-ID": "dd/MM/yyyy",
    "ig-NG": "d/M/yyyy",
    "ii-CN": "yyyy/M/d",
    "is-IS": "d.M.yyyy",
    "it-CH": "dd.MM.yyyy",
    "it-IT": "dd/MM/yyyy",
    "iu-Cans-CA": "d/M/yyyy",
    "iu-Latn-CA": "d/MM/yyyy",
    "ja-JP": "yyyy/MM/dd",
    "ka-GE": "dd.MM.yyyy",
    "kk-KZ": "dd.MM.yyyy",
    "kl-GL": "dd-MM-yyyy",
    "km-KH": "yyyy-MM-dd",
    "kn-IN": "dd-MM-yy",
    "ko-KR": "yyyy. MM. dd",
    "kok-IN": "dd-MM-yyyy",
    "ky-KG": "dd.MM.yy",
    "lb-LU": "dd/MM/yyyy",
    "lo-LA": "dd/MM/yyyy",
    "lt-LT": "yyyy.MM.dd",
    "lv-LV": "yyyy.MM.dd.",
    "mi-NZ": "dd/MM/yyyy",
    "mk-MK": "dd.MM.yyyy",
    "ml-IN": "dd-MM-yy",
    "mn-MN": "yy.MM.dd",
    "mn-Mong-CN": "yyyy/M/d",
    "moh-CA": "M/d/yyyy",
    "mr-IN": "dd-MM-yyyy",
    "ms-BN": "dd/MM/yyyy",
    "ms-MY": "dd/MM/yyyy",
    "mt-MT": "dd/MM/yyyy",
    "nb-NO": "dd.MM.yyyy",
    "ne-NP": "M/d/yyyy",
    "nl-BE": "d/MM/yyyy",
    "nl-NL": "d-M-yyyy",
    "nn-NO": "dd.MM.yyyy",
    "nso-ZA": "yyyy/MM/dd",
    "oc-FR": "dd/MM/yyyy",
    "or-IN": "dd-MM-yy",
    "pa-IN": "dd-MM-yy",
    "pl-PL": "dd.MM.yyyy",
    "prs-AF": "dd/MM/yy",
    "ps-AF": "dd/MM/yy",
    "pt-BR": "d/M/yyyy",
    "pt-PT": "dd-MM-yyyy",
    "qut-GT": "dd/MM/yyyy",
    "quz-BO": "dd/MM/yyyy",
    "quz-EC": "dd/MM/yyyy",
    "quz-PE": "dd/MM/yyyy",
    "rm-CH": "dd/MM/yyyy",
    "ro-RO": "dd.MM.yyyy",
    "ru-RU": "dd.MM.yyyy",
    "rw-RW": "M/d/yyyy",
    "sa-IN": "dd-MM-yyyy",
    "sah-RU": "MM.dd.yyyy",
    "se-FI": "d.M.yyyy",
    "se-NO": "dd.MM.yyyy",
    "se-SE": "yyyy-MM-dd",
    "si-LK": "yyyy-MM-dd",
    "sk-SK": "d. M. yyyy",
    "sl-SI": "d.M.yyyy",
    "sma-NO": "dd.MM.yyyy",
    "sma-SE": "yyyy-MM-dd",
    "smj-NO": "dd.MM.yyyy",
    "smj-SE": "yyyy-MM-dd",
    "smn-FI": "d.M.yyyy",
    "sms-FI": "d.M.yyyy",
    "sq-AL": "yyyy-MM-dd",
    "sr-Cyrl-BA": "d.M.yyyy",
    "sr-Cyrl-CS": "d.M.yyyy",
    "sr-Cyrl-ME": "d.M.yyyy",
    "sr-Cyrl-RS": "d.M.yyyy",
    "sr-Latn-BA": "d.M.yyyy",
    "sr-Latn-CS": "d.M.yyyy",
    "sr-Latn-ME": "d.M.yyyy",
    "sr-Latn-RS": "d.M.yyyy",
    "sv-FI": "d.M.yyyy",
    "sv-SE": "yyyy-MM-dd",
    "sw-KE": "M/d/yyyy",
    "syr-SY": "dd/MM/yyyy",
    "ta-IN": "dd-MM-yyyy",
    "te-IN": "dd-MM-yy",
    "tg-Cyrl-TJ": "dd.MM.yy",
    "th-TH": "d/M/yyyy",
    "tk-TM": "dd.MM.yy",
    "tn-ZA": "yyyy/MM/dd",
    "tr-TR": "dd.MM.yyyy",
    "tt-RU": "dd.MM.yyyy",
    "tzm-Latn-DZ": "dd-MM-yyyy",
    "ug-CN": "yyyy-M-d",
    "uk-UA": "dd.MM.yyyy",
    "ur-PK": "dd/MM/yyyy",
    "uz-Cyrl-UZ": "dd.MM.yyyy",
    "uz-Latn-UZ": "dd/MM yyyy",
    "vi-VN": "dd/MM/yyyy",
    "wo-SN": "dd/MM/yyyy",
    "xh-ZA": "yyyy/MM/dd",
    "yo-NG": "d/M/yyyy",
    "zh-CN": "yyyy/M/d",
    "zh-HK": "d/M/yyyy",
    "zh-MO": "d/M/yyyy",
    "zh-SG": "d/M/yyyy",
    "zh-TW": "yyyy/M/d",
    "zu-ZA": "yyyy/MM/dd",
};
export function getLocaleDateFormat(language, defaultFormat = "dd/MM/yyyy") {
    var _a;
    return (_a = languageFormats[language]) !== null && _a !== void 0 ? _a : defaultFormat;
}
